import React, { useCallback, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { v4 as UUIDv4 } from 'uuid';
import { postsApi } from 'api';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { useProgramPanel } from 'contexts/programPanelContext';
import { PostAttachment, PostAttachmentFileType, PostAttachmentLocal, PostStatus, UserData } from 'types/Dashboard';
import { useAuth } from 'contexts/authContext';
import { DialogConfirm } from 'components/_new/Dialog';
import { ApplicationRoleEnum } from 'types/Application';
import { useGlobal } from 'contexts/globalContext';
import { readPostAttachmentTypeFromFile } from 'utils/postAttachment';
import { MessageBox } from 'components/_new/MessageBox';
import { timelineApi } from 'api/timeline';
import dayjs from 'dayjs';
import { ProgramPostForm, ProgramPostFormValues } from './ProgramPostForm';

export const ProgramPostAdd = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const { current: postLocalUuid } = useRef<string>(UUIDv4());
	const { currentUser } = useAuth();
	const { toastRef } = useGlobal();
	const { data: program } = useProgramPanel();

	const { control, handleSubmit, watch, setValue, reset } = useForm<ProgramPostFormValues>({
		defaultValues: {
			withEmail: true,
			pinned: false,
		},
	});

	const [defaultContent, setDefaultContent] = useState<string | undefined>(undefined);
	const timelineEventRaw = searchParams.get('timelineEvent');
	const timelineEventId =
		timelineEventRaw && timelineEventRaw.length > 0 && !Number.isNaN(Number(timelineEventRaw))
			? Number(timelineEventRaw)
			: null;
	useQuery(
		['TIMELINE_EVENT', { programId: Number(program?.id), eventId: timelineEventId }],
		() => timelineApi.program.getTimelineEventForEdit(Number(program?.id), Number(timelineEventId)),
		{
			enabled: Boolean(timelineEventId),
			onSuccess: (data) => {
				const startTime = data.startAt
					? dayjs(data.startAt).format('DD MMMM HH:mm')
					: dayjs(data.dayDate).format('DD MMMM');
				const subject = `${t('programPanel.post.timelineNewEvent')}: ${data.summary}, ${startTime}`;
				const { recipients, link } = (() => {
					if (data.forApplicationRole === ApplicationRoleEnum.MENTEE) {
						return {
							recipients: 'mentees',
							link: `${process.env.REACT_APP_FRONT_URL}/panel/${program?.name}/mentee/timeline#event-${data.eventId}`,
						};
					}
					if (data.forApplicationRole === ApplicationRoleEnum.MENTOR) {
						return {
							recipients: 'mentors',
							link: `${process.env.REACT_APP_FRONT_URL}/panel/${program?.name}/mentor/timeline#event-${data.eventId}`,
						};
					}
					return {
						recipients: 'all',
						link: `${process.env.REACT_APP_FRONT_URL}/panel/${program?.name}/mentee/timeline#event-${data.eventId}`,
					};
				})();
				const formattedTimeEnd = data.endAt ? dayjs(data.endAt).format('HH:mm') : null;
				const timeInfo = `<p>${t('programPanel.post.timelineTimeInfo')}: <strong>${startTime}${
					formattedTimeEnd ? ` - ${formattedTimeEnd}` : ''
				}</strong></p>`;
				const meetingLink = data.onlineMeetingUrl
					? `<p>${t('programPanel.post.timelineMeetingLink')}: <strong><a href="${data.onlineMeetingUrl}">${
							data.onlineMeetingUrl
					  }</a></strong></p>`
					: null;
				const linkHtml = `<p>${t('programPanel.post.timelineMoreInfo')}: <a href="${link}">${link}</a></p>`;
				const content = [data.description?.trim(), timeInfo, meetingLink, linkHtml]
					.filter((strPart) => strPart && String(strPart).length > 0)
					.join('\n\n');

				setDefaultContent(content);
				reset({ subject, content, recipients, withEmail: true, pinned: false, applications: [] });
			},
			onError: () => {
				// nothing
			},
		}
	);

	const { mutate: addPostMutate, isLoading: addPostLoading } = useMutation(postsApi.addProgramPostAdmin, {
		onSuccess: () => {
			toastRef?.current?.show({
				severity: 'success',
				life: 3000,
				summary: t('misc.success'),
				detail: t('programPanel.post.postHasBeenAdded'),
			});
			navigate(`/program/${program?.name}/communication/list`);
		},
	});

	const { mutate: addPostDraftMutate, isLoading: addPostDraftLoading } = useMutation(postsApi.addProgramPostAdmin, {
		onSuccess: () => {
			toastRef?.current?.show({
				severity: 'success',
				life: 3000,
				summary: t('misc.success'),
				detail: t('programPanel.post.draftHasBeenSaved'),
			});
			navigate(`/program/${program?.name}/communication/list`);
		},
	});

	const handleFormSubmit = handleSubmit((values) => {
		const { withEmail, pinned, ...data } = values;
		addPostMutate({
			localUuid: postLocalUuid,
			programId: program?.id,
			withEmail: Boolean(withEmail),
			pinned: Boolean(pinned),
			status: PostStatus.PUBLISHED,
			...data,
		});
	});

	const handleSaveAsDraft = useCallback(() => {
		handleSubmit((values) => {
			addPostDraftMutate({
				localUuid: postLocalUuid,
				programId: program?.id,
				...values,
				withEmail: false,
				pinned: false,
				status: PostStatus.DRAFT,
			});
		})();
	}, [addPostDraftMutate, handleSubmit, postLocalUuid, program?.id]);

	const isLoading = addPostLoading || addPostDraftLoading;

	const subject = watch('subject');
	const content = watch('content');
	const recipients = watch('recipients');
	const withEmail = watch('withEmail');

	const hasSubject = subject && subject.length > 0;
	const hasContent = content && content.length > 0;

	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

	const getEmailSubject = useCallback(() => {
		if (!subject) return '';

		const mailSenderName = program?.shortName;
		if (mailSenderName && mailSenderName.trim() !== '') {
			return `[${mailSenderName}] ${subject}`;
		}
		return subject;
	}, [subject, program?.shortName]);

	const getNotificationMessage = useCallback(() => {
		const baseMessage = t(
			'programPanel.post.notificationBoardMessage',
			'Wiadomość po dodaniu pojawi się na tablicy powiadomień u adresatów.'
		);

		if (withEmail) {
			const emailSubject = getEmailSubject();
			return t(
				'programPanel.post.notificationBoardWithEmailMessage',
				'Wiadomość po dodaniu pojawi się na tablicy powiadomień u adresatów. Treść zostanie też wysłana do wskazanych adresatów jako wiadomość e-mail o temacie: "{{subject}}".',
				{ subject: emailSubject }
			);
		}

		return baseMessage;
	}, [withEmail, getEmailSubject, t]);

	// upload post section
	const { mutateAsync: addPostAttachmentMutateAsync, isLoading: addPostAttachmentLoading } = useMutation(
		(data: FormData) => postsApi.addProgramPostAttachmentLocalAdmin({ postLocalUuid, data }),
		{
			onSuccess: () => {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('programPanel.post.attachmentUploadSuccessfully'),
				});
			},
		}
	);

	const { mutateAsync: removePostAttachmentMutateAsync } = useMutation(postsApi.removeProgramPostAttachmentAdmin, {
		onSuccess: () => {
			toastRef?.current?.show({
				severity: 'success',
				life: 3000,
				summary: t('misc.success'),
				detail: t('programPanel.post.attachmentRemoveSuccessfully'),
			});
		},
	});

	const [newAttachments, setNewAttachments] = useState<PostAttachment[]>([]);
	const [uploadedAttachments, setUploadedAttachments] = useState<PostAttachmentLocal[]>([]);
	const handleAttachmentUpload = useCallback(
		(file: File) => {
			const formData = new FormData();
			formData.append('file', file);

			const localUuid = UUIDv4();
			const { name: originalFileName, size: originalFileSize, type } = file;
			const fileType = readPostAttachmentTypeFromFile(type);

			const maxFileSize = 1024 * 1024 * 8;
			if (originalFileSize > maxFileSize) {
				toastRef?.current?.show({
					severity: 'error',
					life: 3000,
					summary: 'error',
					detail: t('misc.fileSizeExceeds', { count: 8 }),
				});
				return;
			}
			if (fileType === PostAttachmentFileType.UNKNOWN) {
				toastRef?.current?.show({
					severity: 'error',
					life: 3000,
					summary: 'error',
					detail: t('misc.fileInvalidFileType'),
				});
				return;
			}

			const postAttachmentLocal: PostAttachmentLocal = {
				localUuid,
				postUuidLink: postLocalUuid,
				originalFileName,
				originalFileSize,
				fileType,
				createdAt: new Date(),
			};
			setUploadedAttachments((prev) => [...prev, postAttachmentLocal]);
			addPostAttachmentMutateAsync(formData)
				.then((result) => {
					setUploadedAttachments((prev) => prev.filter((attachment) => attachment.localUuid !== localUuid));
					setNewAttachments((prev) => [...prev, result]);
					return result;
				})
				.catch(() => {
					toastRef?.current?.show({
						severity: 'error',
						life: 3000,
						summary: t('misc.error'),
						detail: t('misc.fileUnexpectedError'),
					});
				})
				.finally(() => {
					setTimeout(() => {
						setUploadedAttachments((prev) =>
							prev.filter((attachment) => attachment.localUuid !== localUuid)
						);
					}, 1000);
				});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[postLocalUuid]
	);

	const handleAttachmentRemove = useCallback(
		(attachment: PostAttachment) =>
			removePostAttachmentMutateAsync(attachment.id)
				.then((result) => {
					setNewAttachments((prev) => prev.filter((newAttachment) => newAttachment.id !== attachment.id));
					return result;
				})
				.catch(() => {
					toastRef?.current?.show({
						severity: 'error',
						life: 3000,
						summary: t('misc.success'),
						detail: t('misc.fileUnexpectedError'),
					});
				}),
		[]
	);

	return (
		<>
			<Helmet title={t('organizationPanel.programCommunicationAddPost')} />

			<DialogConfirm
				visible={confirmDialogOpen}
				onHide={() => setConfirmDialogOpen(false)}
				title={t('programPanel.post.addConfirm')}
				confirmAction={{ submit: true, form: 'add-post-form' }}
			/>
			<SubPageTitle
				title={t('organizationPanel.programCommunicationAddPost')}
				additional={program?.displayName}
				right={
					<Link
						to={`/program/${program?.name}/communication/list`}
						className="p-button p-button-text p-button-sm p-0"
					>
						{t('programPanel.backToList')}
					</Link>
				}
			/>
			<ProgramPostForm
				formId="add-post-form"
				onSubmit={handleFormSubmit}
				control={control}
				setValue={setValue}
				recipients={recipients || ''}
				defaultEditorContent={defaultContent}
				newAttachments={newAttachments}
				uploadedAttachments={uploadedAttachments}
				onAttachmentUpload={handleAttachmentUpload}
				onAttachmentRemove={handleAttachmentRemove}
				isAddingAttachment={addPostAttachmentLoading}
				showWithEmailCheckbox
				showSaveAsDraft
				hasSubject={Boolean(hasSubject)}
				hasContent={Boolean(hasContent)}
				onSaveAsDraft={handleSaveAsDraft}
				onConfirmSubmit={() => setConfirmDialogOpen(true)}
				previewLoading={addPostLoading}
				saveDraftLoading={isLoading}
				submitLoading={addPostLoading}
				submitLabel={t('actions.add')}
				previewUser={currentUser as UserData}
				previewAttachments={newAttachments}
				previewExtra={
					<div className="mt-4">
						<MessageBox
							icon="info-circle"
							iconSet="pi"
							message={getNotificationMessage()}
							variant="purple"
						/>
					</div>
				}
			/>
		</>
	);
};
